import { createSelector, createSelectorCreator } from 'reselect';
import Fuse from 'fuse.js';
import { memoize } from '@comeon/utility';
import { LOGIN_COMPLETE, LOGOUT } from '@comeon/actions-public';
import reducerRegistry from '@comeon/reducer-registry';
import { selectLocale } from '~/components/account/ducks/index.js';
import { createApiAction } from '~/services/middleware/api-middleware.js';
import {
	createEditStreamFields,
	createPrivateStreamFields,
	createStreamFields
} from '../dashboard/session-crud/we-spin-event-form-fields.js';
import {
	DETAIL_VIEW_STATES,
	ENDED,
	PAST_STATES,
	RUNNING,
	UPCOMING_STATES
} from '../constants/we-spin-stream-states.js';
import currencyFormatter from '~/libs/currency-formatter.js';
import { basicApiErrorHandler } from './we-spin-error-handler.js';
import { showNotification } from '~/components/notifications/ducks/notifications.js';
import { WE_SPIN_DASHBOARD_NOTIFICATION_IMAGE_UPLOADED } from '../constants/we-spin-dashboard-text-keys.js';
import { eventTagTransformer } from '../constants/we-spin-event-tags.js';
import {
	normalizedProviderNamesSelector,
	normalizeForSearch,
	searchOptions,
	searchResultsHash
} from '~/components/search/ducks/selectors';
import {
	gameIdxByProviderSelector,
	gameNamesByIdxSelector,
	gamesByIndexSelector
} from '~/components/casino-shared/all-games/ducks/selectors';
import { spectatorRegister } from './we-spin-duck.js';

const SUSPEND_EVENTS_POLLING_TIMEOUT = 3000;

const SORT_ASCENDING = (a, b) => new Date(a.startDate) - new Date(b.startDate);
const SORT_DESCENDING = (a, b) => new Date(b.startDate) - new Date(a.startDate);
export const WE_SPIN_BASE_URL = '/we-spin-api';
const REDUCER_NAME = 'we-spin-dashboard';
const ACTION_PREFIX = '@komigen/we-spin';

// Events
const UPDATE_PROFILE = `${ACTION_PREFIX}/UPDATE_PROFILE`;
const SET_EVENTS_LIST = `${ACTION_PREFIX}/SET_EVENTS_LIST`;
const SET_EVENTS_LIST_PAGINATION = `${ACTION_PREFIX}/SET_EVENTS_LIST_PAGINATION`;
export const CLEAN_EVENT_LIST_PAGINATION = `${ACTION_PREFIX}/CLEAN_EVENT_LIST_PAGINATION`;
const EXTEND_EVENTS_LIST = `${ACTION_PREFIX}/EXTEND_EVENTS_LIST`;
const SET_EVENT = `${ACTION_PREFIX}/SET_EVENT`;
const SET_PROFILE = `${ACTION_PREFIX}/SET_PROFILE`;
const SET_IS_STREAMER = `${ACTION_PREFIX}/SET_IS_STREAMER`;
const SET_IS_BLOCKED_STREAMER = `${ACTION_PREFIX}/SET_IS_BLOCKED_STREAMER`;
const SET_INITIAL_DEFAULT_VALUES = `${ACTION_PREFIX}/SET_INITIAL_DEFAULT_VALUES`;
const RESET_EDIT_STREAM_DEFAULT_VALUES = `${ACTION_PREFIX}/RESET_EDIT_STREAM_DEFAULT_VALUES`;
const SET_EDIT_STREAM_DEFAULT_VALUES = `${ACTION_PREFIX}/SET_EDIT_STREAM_DEFAULT_VALUES`;
const SET_SKIP_NEXT_POLLING_UPDATE = `${ACTION_PREFIX}/SET_SKIP_NEXT_POLLING_UPDATE`;
const SET_IS_DASHBOARD_INITIALIZING = `${ACTION_PREFIX}/SET_IS_DASHBOARD_INITIALIZING`;
export const LIVE_DASHBOARD_DATA = `${ACTION_PREFIX}/LIVE_DASHBOARD_DATA`;
export const SET_IS_STREAMER_LIVE = `${ACTION_PREFIX}/SET_IS_STREAMER_LIVE`;
const UPDATE_SEARCH_RESULTS = `${ACTION_PREFIX}/UPDATE_SEARCH_RESULTS`;
const SET_IS_LIVE_DASHBOARD_DATA_POLLING = `${ACTION_PREFIX}/SET_IS_LIVE_DASHBOARD_DATA_POLLING`;
const SET_SHOW_REMOVE_EVENT_POPUP = `${ACTION_PREFIX}/SET_SHOW_REMOVE_EVENT_POPUP`;
const SET_SELECTED_ACTION_SHEET_EVENT = `${ACTION_PREFIX}/SET_SELECTED_ACTION_SHEET_EVENT`;
const SET_ENDED_LIVESTREAM_ID = `${ACTION_PREFIX}/SET_ENDED_LIVESTREAM_ID`;
const SET_ENDED_EVENT = `${ACTION_PREFIX}/SET_ENDED_EVENT`;
const DASHBOARD_INITIALIZED = `${ACTION_PREFIX}/DASHBOARD_INITIALIZED`;
export const FETCH_EVENTS_INTERVAL = `${ACTION_PREFIX}/FETCH_EVENTS_INTERVAL`;

const searchSelectorCreator = createSelectorCreator(memoize, searchResultsHash);

// Selectors
export const selectCurrentWeSpinBalance = state => state[REDUCER_NAME].liveDashboardData?.currentBalance;
export const selectIsLiveDashboardDataPolling = state => state[REDUCER_NAME].isLiveDashboardDataPolling;
export const selectCurrentLivestreamId = state => state[REDUCER_NAME].profile?.liveStreamId;
export const selectTargetBrands = state => state[REDUCER_NAME].profile?.targetBrands || [];
export const selectEndedLivestreamId = state => state[REDUCER_NAME].endedLivestreamId;
export const selectEventsList = state => state[REDUCER_NAME].eventsList;
export const selectEventsListPagination = state => state[REDUCER_NAME].eventsListPagination;
export const selectEventByRef = eventRef => state =>
	state[REDUCER_NAME].eventsList?.find(e => e.streamRef === eventRef);
export const selectEventsListCount = state => state[REDUCER_NAME].eventsList?.length || 0;
export const selectShowRemoveEventPopup = state => state[REDUCER_NAME].showRemoveEventPopup;
export const selectSelectedActionSheetEvent = state => state[REDUCER_NAME].selectedActionSheetEvent;
export const selectIsDashboardInitialized = state => state[REDUCER_NAME].isDashboardInitialized;
export const selectStreamerChatUrl = state => state[REDUCER_NAME].profile?.streamerChatUrl;
export const selectEventsPollingSuspended = state => state[REDUCER_NAME].eventsPollingSuspended;
export const selectEndedEvent = state => state[REDUCER_NAME].endedEvent;

export const setEndedLivestreamId = livestreamId => dispatch =>
	dispatch({
		type: SET_ENDED_LIVESTREAM_ID,
		payload: livestreamId
	});

export const setEndedEvent = event => ({
	type: SET_ENDED_EVENT,
	payload: event
});

export const setEventsPollingSuspended = needSkip => dispatch =>
	dispatch({
		type: SET_SKIP_NEXT_POLLING_UPDATE,
		payload: needSkip
	});

export const setIsDashboardInitializing = isPending => dispatch =>
	dispatch({
		type: SET_IS_DASHBOARD_INITIALIZING,
		payload: isPending
	});

const setEventsList = events => ({
	type: SET_EVENTS_LIST,
	payload: events
});

const extendEventsList = events => ({
	type: EXTEND_EVENTS_LIST,
	payload: events
});

const setEventListPagination = pagination => ({
	type: SET_EVENTS_LIST_PAGINATION,
	payload: pagination
});

export const setEvent = event => ({
	type: SET_EVENT,
	payload: event
});

export const setShowRemoveEventPopup = boolean => ({
	type: SET_SHOW_REMOVE_EVENT_POPUP,
	payload: boolean
});

export const setSelectedActionSheetEvent = event => ({
	type: SET_SELECTED_ACTION_SHEET_EVENT,
	payload: event
});

export const setIsStreamerLive = boolean => ({
	type: SET_IS_STREAMER_LIVE,
	payload: boolean
});

export const setIsStreamer = boolean => ({
	type: SET_IS_STREAMER,
	payload: boolean
});

export const setIsBlockedStreamer = boolean => ({
	type: SET_IS_BLOCKED_STREAMER,
	payload: boolean
});

export const setProfile = profile => ({
	type: UPDATE_PROFILE,
	payload: profile
});

export const searchResultSelector = searchTerm =>
	searchSelectorCreator(
		[gameNamesByIdxSelector, normalizedProviderNamesSelector, gameIdxByProviderSelector],
		(gameNames, providerNames, gameIdxByProvider) => {
			if (searchTerm) {
				const normalizedSearchTerm = normalizeForSearch(searchTerm);
				const normalizedProviderNames = Object.keys(providerNames);
				if (normalizedProviderNames.includes(normalizedSearchTerm)) {
					return gameIdxByProvider[providerNames[normalizedSearchTerm]];
				}
				const gameNameIndex = new Fuse(gameNames, searchOptions);
				const searchResult = gameNameIndex.search(searchTerm).map(item => item.refIndex);
				return searchResult;
			}
			return [];
		}
	);

export const selectSearchResults = state => state[REDUCER_NAME].searchResults;
export const selectInitialCreateStreamValues = state => state[REDUCER_NAME].initialCreateStreamValues;
export const selectInitialEditStreamValues = state => state[REDUCER_NAME].initialEditStreamValues;
export const selectIsTester = state => state[REDUCER_NAME].profile?.isTester;

export const selectLiveEvent = state => {
	return selectEventsList(state).find(({ state: s }) => s === RUNNING);
};

export const selectIsEventLive = state => {
	return !!selectLiveEvent(state);
};

export const selectProfile = state => ({
	...state[REDUCER_NAME].profile,
	isPrivate:
		!!state[REDUCER_NAME].profile?.private ||
		(localStorage.getItem('profile.isPrivate') === 'true' && !state[REDUCER_NAME].isStreamer)
});

export const selectIsBlockedStreamer = state => state[REDUCER_NAME].isBlockedStreamer;

export const selectTwitchAuth = state => state[REDUCER_NAME].profile.isTwitchAuthtorized;

export const selectProfileNoImgUrls = state => {
	const { streamerBgUrl, streamerImageUrl, ...profile } = state[REDUCER_NAME].profile;
	return { ...profile };
};
export const selectIsLive = state => state[REDUCER_NAME].profile?.isLive;

export const selectScheduledStream = streamRef => state =>
	selectEventsList(state)
		.filter(stream => stream.streamRef === streamRef)
		.find(Boolean);

export const selectIsStreamer = state => state[REDUCER_NAME].isStreamer;

export const selectLiveDashboardData = state => state[REDUCER_NAME].liveDashboardData;

export const selectUpcomingEvents = createSelector(selectEventsList, eventsList =>
	eventsList
		.filter(({ state, startDate }) => {
			const isOneDayLeft = (new Date(startDate) - new Date()) / (1000 * 60 * 60 * 24) < 1;
			return DETAIL_VIEW_STATES.includes(state) && isOneDayLeft;
		})
		.sort(SORT_ASCENDING)
);

export const selectDetailViewEvent = createSelector(
	[selectLiveEvent, selectUpcomingEvents],
	(liveEvent, upcomingEvents) => {
		return liveEvent || upcomingEvents.sort(SORT_ASCENDING).find(e => DETAIL_VIEW_STATES.includes(e.state));
	}
);

export const selectUpcomingEventsButDetailViewEvent = createSelector(
	[selectUpcomingEvents, selectDetailViewEvent],
	(upcomingEvents, detailViewEvent) =>
		upcomingEvents.filter(({ streamRef }) => streamRef !== detailViewEvent?.streamRef)
);

export const selectEndedEventFromLivestream = createSelector(
	[selectEventsList, selectCurrentLivestreamId],
	(events, liveStreamId) => {
		if (!liveStreamId) return null;
		return events.sort(SORT_DESCENDING).find(event => event.liveStreamId === liveStreamId && event.state === ENDED);
	}
);

export const selectIsDashboardInitializing = state => state[REDUCER_NAME].isDashboardInitializing;

export const getInitialDefaultValues = (fields, currencyCode) =>
	fields.reduce((obj, field) => {
		// eslint-disable-next-line no-param-reassign
		obj[field.id] =
			(field.defaultValue && typeof field.defaultValue === 'function' ? field.defaultValue() : '') ||
			field.defaultValue ||
			'';

		if (field.id === 'balanceCap' && currencyCode !== undefined) {
			// eslint-disable-next-line no-param-reassign
			field.label += ` (${currencyCode})`;
		}

		return obj;
	}, {});

const leadingZero = str => `0${str}`.slice(-2);

export const resetEditEventDefaultValues = () => ({
	type: RESET_EDIT_STREAM_DEFAULT_VALUES
});
export const getEditEventInitialValues = eventRef =>
	createApiAction({
		onStart: resetEditEventDefaultValues,
		endpoint: `${WE_SPIN_BASE_URL}/streamer/getEvent?streamRef=${eventRef}`,
		onError: basicApiErrorHandler,
		onSuccess: original => {
			const startDate = new Date(original.startDate);
			const defaultValues = {
				...getInitialDefaultValues(createEditStreamFields()),
				eventTag: original.eventTags.find(x => x),
				gameCsv: original.games,
				boost: original.boostStreamerView,
				durationInMinutes: original.durationInMinutes,
				balanceCap: original.targetBalance,
				startingTime: `${leadingZero(startDate.getHours())}:${leadingZero(startDate.getMinutes())}`,
				startingDate: `${startDate.getFullYear()}-${leadingZero(startDate.getMonth() + 1)}-${leadingZero(
					startDate.getDate()
				)}`,
				streamBgUrl: original?.streamBgUrl,
				betSizeRangeMin: original.betSizeRangeMin,
				betSizeRangeMax: original.betSizeRangeMax
			};
			return {
				type: SET_EDIT_STREAM_DEFAULT_VALUES,
				payload: Object.entries(defaultValues).reduce((obj, [k, v]) => {
					// eslint-disable-next-line no-param-reassign
					obj[k] = original[k] || v;
					return obj;
				}, {})
			};
		}
	});

const uninitializedCreateEventInitialSuccessHandler =
	isPrivateStreamer =>
	({
		availableSessionBalanceBoosts,
		languages,
		currencyCode,
		defaultStreamCreate: { startTime, publishDate, registrationTime, streamUrl, ...rest }
	}) => {
		/* TODO: maybe better solution to drop default game for private streams? */
		const other = {
			...rest
			// gameCsv: '' // FIXME Temporary thing for testing DK, remove me
		};
		if (isPrivateStreamer) {
			delete other.gameCsv;
		}
		return {
			type: SET_INITIAL_DEFAULT_VALUES,
			payload: {
				...getInitialDefaultValues(
					!isPrivateStreamer ? createStreamFields() : createPrivateStreamFields(),
					currencyCode
				),
				...other,
				streamUrl: streamUrl || '',
				languages,
				language: languages?.length > 0 ? languages[0] : null,
				availableSessionBalanceBoosts,
				currencyCode,
				initialized: true
			}
		};
	};

const initializedCreateEventInitialSuccessHandler =
	isPrivateStreamer =>
	({ availableSessionBalanceBoosts, currencyCode, defaultStreamCreate: { ...rest } }) => {
		const { startingDate, startingTime } = getInitialDefaultValues(
			!isPrivateStreamer ? createStreamFields() : createPrivateStreamFields(),
			currencyCode
		);
		const other = {
			...rest
		};
		if (isPrivateStreamer) {
			delete other.gameCsv;
		}
		return {
			type: SET_INITIAL_DEFAULT_VALUES,
			payload: {
				startingDate,
				startingTime,
				availableSessionBalanceBoosts
				// gameCsv: rest.gameCsv
			}
		};
	};

export const fetchCreateStreamInitialValues = () => (dispatch, getState) => {
	const { initialized } = selectInitialCreateStreamValues(getState());
	const { isPrivate: isPrivateStreamer } = selectProfile(getState());
	return dispatch(
		createApiAction({
			endpoint: `${WE_SPIN_BASE_URL}/streamer/getConfigOptions`,
			onError: basicApiErrorHandler,
			onSuccess: initialized
				? initializedCreateEventInitialSuccessHandler(isPrivateStreamer)
				: uninitializedCreateEventInitialSuccessHandler(isPrivateStreamer)
		})
	);
};

export const updateProfile = payload => (dispatch, getState) => {
	const currentProfile = selectProfile(getState());

	const { description, screenName, streamUrl, streamerBgUrl, streamerChatUrl, streamerImageUrl } = {
		...currentProfile,
		...payload
	};

	return dispatch(
		createApiAction({
			endpoint: `${WE_SPIN_BASE_URL}/streamer/updateProfile`,
			method: 'POST',
			payload: {
				description,
				screenName,
				streamUrl,
				streamerBgUrl,
				streamerChatUrl,
				streamerImageUrl
			},
			onSuccess: setProfile,
			onError: basicApiErrorHandler
		})
	);
};

export const uploadImageNew = async (file, hash = false) => {
	const formData = new FormData();
	formData.append('file', file);
	if (hash) {
		formData.append('hash', hash);
	}
	const response = await fetch(`${WE_SPIN_BASE_URL}/streamer/uploadImage`, {
		body: formData,
		method: 'post'
	});
	if (!response.ok) {
		throw new Error(response.statusCode);
	}
	return (await response.json())?.url || '';
};

export const uploadImage = (field, file) => {
	const formData = new FormData();
	formData.append('file', file);

	return createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/streamer/uploadImage`,
		method: 'POST',
		payload: formData,
		options: {
			urlencodedContentType: true
		},
		onSuccess:
			({ url }) =>
			(dispatch, getState) => {
				const profile = { ...selectProfile(getState()) };
				profile[field] = url;
				dispatch(setProfile(profile));
			},
		onError: basicApiErrorHandler
	});
};

export const uploadImageOnChange = (fileInput, isStreamerImage, hash = false) => {
	const formData = new FormData();
	formData.append('file', fileInput.files[0]);
	if (hash) {
		formData.append('hash', hash);
	}

	return createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/streamer/uploadImage`,
		method: 'POST',
		payload: formData,
		options: {
			urlencodedContentType: true
		},
		onSuccess:
			({ url }) =>
			(dispatch, getState) => {
				const profile = { ...selectProfile(getState()) };
				profile[isStreamerImage ? 'streamerImageUrl' : 'streamerBgUrl'] = url;
				dispatch(updateProfile(profile));
				dispatch(
					showNotification({
						openInSidebar: true,
						color: 'green',
						autoHide: true,
						messageKey: WE_SPIN_DASHBOARD_NOTIFICATION_IMAGE_UPLOADED
					})
				);
			},
		onError: basicApiErrorHandler
	});
};

export const fetchIsStreamer = () => dispatch => {
	return dispatch(
		createApiAction({
			endpoint: `${WE_SPIN_BASE_URL}/view/isStreamer`,
			onSuccess: setIsStreamer
		})
	);
};

export const fetchIsBlockedStreamer = () => dispatch => {
	return dispatch(
		createApiAction({
			endpoint: `${WE_SPIN_BASE_URL}/view/isBlockedStreamer`,
			onSuccess: setIsBlockedStreamer
		})
	);
};

export const fetchProfile = () =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/streamer/getProfile`,
		onSuccess: response => ({
			type: SET_PROFILE,
			payload: response
		}),
		onError: basicApiErrorHandler
	});

export const fetchTwitchAuthorization = () =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/view/isTwitchAuthorized`,
		onSuccess: response => (dispatch, getState) => {
			const profile = { ...selectProfile(getState()), isTwitchAuthtorized: response };
			dispatch(setProfile(profile));
		}
	});

export const fetchEventsList = (states = null, replace = true, pooling = false, pageSize = 15, pageNo) => {
	let endpoint = `${WE_SPIN_BASE_URL}/streamer/getEvents?pageSize=${pageSize}${
		pageNo ? `&pageNo=${pageNo.toString()}` : ''
	}`;
	if (states) {
		endpoint += `&states=${states.join(',')}`;
	}
	return createApiAction({
		endpoint,
		onSuccess: response => (dispatch, getState) => {
			if (!pooling) {
				dispatch(
					setEventListPagination({
						pageNo: response.pageNo + 1,
						pageSize: response.pageSize,
						totalItems: response.totalItems,
						totalPages: response.totalPages
					})
				);
			}
			if (replace) {
				if (!selectEventsPollingSuspended(getState())) {
					dispatch(setEventsList(response.data));
				}
			} else {
				dispatch(extendEventsList(response.data));
			}
		},
		onError: basicApiErrorHandler
	});
};

const lazyInitFetchEvents = () => dispatch => {
	dispatch(setEventsList([]));
	dispatch(fetchEventsList(PAST_STATES, false));
	return dispatch(fetchEventsList(UPCOMING_STATES, false));
};

export const initializeDashboard = () => async (dispatch, getState) => {
	await dispatch(fetchIsStreamer());
	await dispatch(fetchIsBlockedStreamer());
	if (selectIsStreamer(getState())) {
		await dispatch(fetchProfile());
	}
	if (selectIsStreamer(getState()) && !selectIsDashboardInitializing(getState())) {
		dispatch(setIsDashboardInitializing(true));
		await Promise.all([dispatch(lazyInitFetchEvents()), dispatch(fetchProfile())]).catch(error =>
			// eslint-disable-next-line no-console
			console.warn(error)
		);
	}
	dispatch(setIsDashboardInitializing(false));
	dispatch(setEventsPollingSuspended(false));
	dispatch({
		type: DASHBOARD_INITIALIZED
	});
};

// Init dashboard if not done yet
export const initDashboard = () => async (dispatch, getState) => {
	if (!selectIsDashboardInitialized(getState())) {
		dispatch(initializeDashboard());
	}
};

export const fetchEvent = eventRef =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/streamer/getEvent?streamRef=${eventRef}`,
		onSuccess: setEvent,
		onError: basicApiErrorHandler
	});

export const fetchEndedEvent = eventRef =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/streamer/getEvent?streamRef=${eventRef}`,
		onSuccess: setEndedEvent,
		onError: basicApiErrorHandler
	});
export const suspendEventsPolling = () => (dispatch, getState) => {
	if (!selectEventsPollingSuspended(getState())) {
		dispatch(setEventsPollingSuspended(true));
		setTimeout(() => {
			dispatch(setEventsPollingSuspended(false));
		}, SUSPEND_EVENTS_POLLING_TIMEOUT);
	}
};

export const removeEvent = streamRef =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/streamer/cancelEvent?streamRef=${streamRef}`,
		method: 'POST',
		payload: {},
		onError: basicApiErrorHandler,
		onSuccess: () => dispatch => {
			dispatch(suspendEventsPolling());
			dispatch(fetchEvent(streamRef));
			dispatch(
				showNotification({
					openInSidebar: true,
					color: 'green',
					autoHide: true,
					messageKey: 'WE_SPIN_DASHBOARD_EVENT_REMOVED'
				})
			);
		}
	});

export const updateEvent = (streamRef, data) => (dispatch, getState) => {
	const scheduledEvent = selectScheduledStream(streamRef)(getState());
	const { startingDate, startingTime, eventTag = '', ...rest } = data;
	const startTime = new Date(`${startingDate}T${startingTime}`).toISOString();
	const publishTime = startTime;

	const event = eventTagTransformer({
		...rest,
		startTime,
		publishTime,
		eventTags: [eventTag].filter(Boolean),
		registrationTime: scheduledEvent.registrationTime
	});

	return dispatch(
		createApiAction({
			endpoint: `${WE_SPIN_BASE_URL}/streamer/updateEvent?streamRef=${streamRef}`,
			method: 'POST',
			payload: event,
			onError: basicApiErrorHandler,
			onSuccess: () => () => {
				dispatch(suspendEventsPolling());
				dispatch(fetchEvent(streamRef));
			}
		})
	);
};

export const createEvent = (payload, buyIn) => {
	const { startingTime, startingDate, publishingTime, eventTag, ...rest } = payload;
	const registrationTime = new Date().toISOString();
	const startTime = new Date(`${startingDate}T${startingTime}`).toISOString();
	const publishDate = startTime;
	const latency = 5;

	const event = eventTagTransformer({
		...rest,
		startTime,
		publishDate,
		registrationTime,
		latency,
		eventTags: [eventTag].filter(Boolean)
	});

	return createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/streamer/createEvent`,
		method: 'POST',
		payload: event,
		onSuccess:
			({ streamRef }) =>
			async dispatch => {
				// Apply optional inline buy in
				if (buyIn) {
					await dispatch(spectatorRegister(buyIn, streamRef));
				}
				dispatch(
					showNotification({
						openInSidebar: true,
						color: 'green',
						autoHide: true,
						messageKey: 'WE_SPIN_DASHBOARD_CREATION_SUCCESS_CONTENT'
					})
				);
				await dispatch(fetchEvent(streamRef));
			}
	});
};

export const fetchLiveDashboardData = () => (dispatch, getState) => {
	const streamRef = selectLiveEvent(getState())?.streamRef;

	if (!streamRef) return;

	const onSuccessHandler =
		({ result, currentBalance, currency, stopTime, startTime, noPlayers, betSize, startBalance, eventTags = [] }) =>
		() => {
			const locale = selectLocale(getState());

			const currentBalanceFormatted = currencyFormatter.format(currentBalance, currency, locale);

			const netResultFormatted = currencyFormatter.format(result, currency, locale);

			const betSizeFormatted = currencyFormatter.format(betSize, currency, locale);

			const startBalanceFormatted = currencyFormatter.format(startBalance, currency, locale);

			dispatch({
				type: LIVE_DASHBOARD_DATA,
				payload: {
					result: netResultFormatted,
					currentBalance: currentBalanceFormatted,
					stopTime,
					startTime,
					noPlayers,
					betSize: betSizeFormatted,
					startBalance: startBalanceFormatted,
					eventTags
				}
			});
		};

	return dispatch(
		createApiAction({
			endpoint: `${WE_SPIN_BASE_URL}/streamer/dashboard?streamRef=${streamRef}`,
			onSuccess: onSuccessHandler,
			onError: basicApiErrorHandler
		})
	);
};

export const becomePrivateStreamer = () =>
	createApiAction({
		endpoint: `${WE_SPIN_BASE_URL}/player/becomePrivateStreamer`,
		method: 'POST',
		payload: {},
		onSuccess: () => dispatch => {
			localStorage.removeItem('profile.isPrivate');
			dispatch(setIsStreamer(true));
		}
	});

export const updateSearchResults = searchTerm => (dispatch, getState) => {
	const indexes = searchResultSelector(searchTerm)(getState());
	const gamesByIdx = gamesByIndexSelector(getState());
	const results = indexes.map(idx => gamesByIdx[idx]);

	return dispatch({
		type: UPDATE_SEARCH_RESULTS,
		payload: results
	});
};

export const subscribeToLiveDashboardData = pollingInterval => (dispatch, getState) => {
	const isLiveDashboardDataPolling = selectIsLiveDashboardDataPolling(getState());

	if (isLiveDashboardDataPolling) return null;

	dispatch({
		type: SET_IS_LIVE_DASHBOARD_DATA_POLLING,
		payload: true
	});

	const id = setInterval(() => {
		dispatch(fetchLiveDashboardData());
	}, pollingInterval);

	return () => {
		clearInterval(id);
		dispatch({
			type: SET_IS_LIVE_DASHBOARD_DATA_POLLING,
			payload: false
		});
	};
};

const initialState = {
	liveDashboardData: undefined,
	isLiveDashboardDataPolling: false,
	eventsPollingSuspended: true,
	isStreamer: undefined,
	eventsList: [],
	eventsListPagination: null,
	profile: {
		hydrated: false,
		loading: true,
		description: '',
		screenName: '',
		streamUrl: '',
		streamerChatUrl: '',
		streamerBgUrl: '',
		streamerImageUrl: '',
		isTwitchAuthtorized: null
	},
	initialCreateStreamValues: getInitialDefaultValues(createStreamFields()),
	initialEditStreamValues: null,
	searchResults: [],
	showRemoveEventPopup: false,
	selectedActionSheetEvent: null,
	isDashboardInitialized: false,
	endedLivestreamId: null,
	endedEvent: null,
	isDashboardInitializing: false
};

const reducer = (state = initialState, { type, payload }) => {
	switch (type) {
		case LOGIN_COMPLETE:
		case LOGOUT:
			return { ...state, isStreamer: undefined };
		case LIVE_DASHBOARD_DATA:
			return { ...state, liveDashboardData: payload };
		case SET_IS_STREAMER:
			return { ...state, isStreamer: payload };
		case SET_IS_BLOCKED_STREAMER:
			return { ...state, isBlockedStreamer: payload };
		case SET_EVENTS_LIST:
			return { ...state, eventsList: payload };
		case SET_EVENTS_LIST_PAGINATION:
			return { ...state, eventsListPagination: payload };
		case CLEAN_EVENT_LIST_PAGINATION:
			return { ...state, eventsListPagination: null };
		case EXTEND_EVENTS_LIST:
			return {
				...state,
				eventsList: payload.reduce(
					(acc, event) => {
						const existingIndex = acc.findIndex(e => e.streamRef === event.streamRef);
						if (existingIndex !== -1) {
							acc[existingIndex] = {
								...acc[existingIndex],
								...event
							};
						} else {
							acc.push(event);
						}
						return acc;
					},
					[...state.eventsList]
				)
			};
		case SET_EVENT: {
			const eventRef = payload.streamRef;
			const eventIndex = state.eventsList.findIndex(e => e.streamRef === eventRef);
			const newEventsList = [...state.eventsList];
			if (eventIndex !== -1) {
				newEventsList[eventIndex] = payload;
			} else {
				newEventsList.push(payload);
			}
			return { ...state, eventsList: newEventsList };
		}
		case DASHBOARD_INITIALIZED:
			return {
				...state,
				isDashboardInitialized: true
			};
		case SET_PROFILE:
		case UPDATE_PROFILE: {
			const { tester, live, ...rest } = payload;
			return {
				...state,
				profile: {
					...state.profile,
					loading: false,
					hydrated: true,
					isTester: tester,
					isLive: live,
					...rest
				}
			};
		}
		case SET_INITIAL_DEFAULT_VALUES:
			return {
				...state,
				initialCreateStreamValues: {
					...state.initialCreateStreamValues,
					...payload
				}
			};
		case RESET_EDIT_STREAM_DEFAULT_VALUES:
			return { ...state, initialEditStreamValues: null };
		case SET_EDIT_STREAM_DEFAULT_VALUES:
			return { ...state, initialEditStreamValues: payload };
		case SET_IS_STREAMER_LIVE:
			return { ...state, profile: { ...state.profile, isLive: payload } };
		case UPDATE_SEARCH_RESULTS:
			return { ...state, searchResults: payload };
		case SET_IS_LIVE_DASHBOARD_DATA_POLLING:
			return { ...state, isLiveDashboardDataPolling: payload };
		case SET_SHOW_REMOVE_EVENT_POPUP:
			return {
				...state,
				showRemoveEventPopup: payload
			};
		case SET_SELECTED_ACTION_SHEET_EVENT:
			return {
				...state,
				selectedActionSheetEvent: payload
			};
		case SET_ENDED_LIVESTREAM_ID:
			return {
				...state,
				endedLivestreamId: payload
			};
		case SET_ENDED_EVENT:
			return {
				...state,
				endedEvent: payload
			};
		case SET_SKIP_NEXT_POLLING_UPDATE:
			return {
				...state,
				eventsPollingSuspended: payload
			};
		case SET_IS_DASHBOARD_INITIALIZING:
			return {
				...state,
				isDashboardInitializing: payload
			};
		default:
			return state;
	}
};

reducerRegistry.register(REDUCER_NAME, reducer);

export default reducer;
